import React, { useState } from 'react';
import { Crown, Zap, Settings } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import UpgradeModal from './UpgradeModal';
import { colors, typography, spacing, radius, components } from '../../styles/theme';

export function SubscriptionCard() {
  const { 
    tier, 
    subscriptionStatus, 
    openPortal,
    upgradeAccount,
    tierRules
  } = useSubscription();
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const getTierColor = () => {
    switch (tier) {
      case 'paid': return '#10b981';
      case 'free': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getTierIcon = () => {
    switch (tier) {
      case 'paid': return <Crown size={24} color="#fbbf24" />;
      case 'free': return <Zap size={24} color="#3b82f6" />;
      default: return null;
    }
  };

  const getTierLabel = () => {
    switch (tier) {
      case 'paid': return 'Pro';
      case 'free': return 'Free';
      case 'anonymous': return 'Trial';
      default: return 'Unknown';
    }
  };

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.3)',
      backdropFilter: 'blur(10px)',
      border: `1px solid ${colors.borderLight}`,
      borderRadius: radius.lg,
      padding: spacing[4],
      fontFamily: typography.fontSans
    }}>
      {/* Minimal Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: spacing[3],
        paddingBottom: spacing[2],
        borderBottom: `1px solid ${colors.borderLight}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          {tier === 'paid' && <Crown size={14} color={colors.warning} />}
          {tier === 'free' && <Zap size={14} color={colors.primary} />}
          <span style={{ 
            fontSize: typography.xs, 
            fontWeight: typography.semibold,
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {getTierLabel()}
          </span>
        </div>
        
        {tier === 'paid' && (
          <button
            onClick={openPortal}
            style={{
              padding: spacing[1],
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: colors.textTertiary,
              transition: 'color 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = colors.textSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = colors.textTertiary;
            }}
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Tier Info - More subtle */}
      <div style={{ 
        fontSize: typography.xs, 
        color: colors.textTertiary,
        lineHeight: typography.relaxed,
        marginBottom: spacing[3]
      }}>
        {tier === 'paid' && 'Unlimited • All dimensions'}
        {tier === 'free' && '8/day • 3 dimensions'}
        {tier === 'anonymous' && '3/day • 2 dimensions'}
      </div>

      {/* Upgrade CTA for Free Users */}
      {tier === 'free' && (
        <button
          onClick={() => setShowUpgradeModal(true)}
          style={{
            width: '100%',
            padding: spacing[2],
            background: 'transparent',
            color: colors.textSecondary,
            border: `1px solid ${colors.borderLight}`,
            borderRadius: radius.sm,
            fontSize: typography.xs,
            fontWeight: typography.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            transition: 'all 0.15s ease',
            fontFamily: typography.fontSans,
            letterSpacing: '0.01em'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(251, 191, 36, 0.05)';
            e.currentTarget.style.borderColor = colors.warning;
            e.currentTarget.style.color = colors.warning;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = colors.borderLight;
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <Crown size={12} />
          Upgrade to Pro
        </button>
      )}

      {/* Subscription Status for Paid Users - Minimalist */}
      {tier === 'paid' && subscriptionStatus && subscriptionStatus.hasSubscription && (
        <div style={{ marginBottom: spacing[3] }}>
          <div style={{ 
            fontSize: typography.sm, 
            fontWeight: typography.semibold, 
            color: subscriptionStatus.cancelAtPeriodEnd ? colors.warning : colors.accent,
            marginBottom: spacing[1]
          }}>
            {subscriptionStatus.displayStatus || (subscriptionStatus.status === 'active' ? 'Active' : subscriptionStatus.status)}
          </div>
          {subscriptionStatus.plan && (
            <div style={{ 
              fontSize: typography.xs, 
              color: colors.textTertiary 
            }}>
              {subscriptionStatus.plan === 'monthly' ? 'Monthly' : 'Annual'}
            </div>
          )}
        </div>
      )}

      {/* Upgrade CTA for Anonymous - Ghost button */}
      {tier === 'anonymous' && (
        <button
          onClick={() => setShowUpgradeModal(true)}
          style={{
            width: '100%',
            padding: spacing[2],
            background: 'transparent',
            color: colors.textSecondary,
            border: `1px solid ${colors.borderLight}`,
            borderRadius: radius.sm,
            fontSize: typography.xs,
            fontWeight: typography.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            transition: 'all 0.15s ease',
            fontFamily: typography.fontSans,
            letterSpacing: '0.01em'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(251, 191, 36, 0.05)';
            e.currentTarget.style.borderColor = colors.warning;
            e.currentTarget.style.color = colors.warning;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = colors.borderLight;
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <Crown size={12} />
          Upgrade to Pro
        </button>
      )}
      
      {/* Modals */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
}

export default SubscriptionCard;

